"use client";

import {
  Box,
  Button,
  Field,
  Flex,
  Text,
  Textarea,
  VStack,
} from "@chakra-ui/react";
import { Highlight } from "react-instantsearch";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  type ConfirmThoughtsRequest,
  type MeiliIndex,
  type MeiliSuggestedThought,
} from "@/generated/fastapi";
import {
  type MeiliDocumentSearchHit,
  MEILI_SEARCH_CONFIG,
} from "@/features/search";
import { useSearchQueryCheck, useUserChoices } from "@/state/globalStore";
import { showErrorToast } from "@/shared/feedback";
import { useSystemDesign } from "@/shared/system-design";
import appSettings from "@/shared/config/appSettings";
import { useThoughtRefresh } from "@/features/thoughts";
import { useSituationsQuery } from "@/features/situations";
import { PROFILE_SHAPING_LABELS } from "@/features/profile-shaping";
import { CONTENT_ITEM_FOCUS_VISIBLE_STYLES } from "@/shared/ui/display/ContentItemPrimitives";
import { CONTENT_ITEM_HOVER_STYLES } from "@/shared/ui/display/ContentItemPrimitives";
import { CONTENT_ITEM_HIGHLIGHT_STYLES } from "@/shared/ui/display/ContentItemPrimitives";
import { CONTENT_ITEM_TEXT_PROPS } from "@/shared/ui/display/ContentItemPrimitives";
import ContentItemCard from "@/shared/ui/display/ContentItemCard";
import { ContentItemPreviewActions } from "@/shared/ui/display/ContentItemPrimitives";
import { ContentItemUnassignedCallout } from "@/shared/ui/display/ContentItemPrimitives";
import GenericIcon from "@/shared/ui/actions/GenericIcon";
import WithSwipeSelector from "@/shared/ui/gestures/WithSwipeSelector";
import { useSuggestedThoughtConfirmationContext } from "../contexts/SuggestedThoughtConfirmationProvider";
import useSuggestedThoughtActions from "../hooks/useSuggestedThoughtActions";
import useSuggestedThoughtItemActions from "../hooks/useSuggestedThoughtItemActions";
import ThoughtHighlightedText from "./ThoughtHighlightedText";

const THOUGHT_PREVIEW_LINE_CLAMP = { base: 5, md: 4 } as const;
const MOBILE_ACTION_TOGGLE_POINTER_MOVE_THRESHOLD = 12;

function getEditedMindstring(
  originalMindstring: string,
  nextMindstring: string,
): string | undefined {
  return nextMindstring !== originalMindstring ? nextMindstring : undefined;
}

type SuggestedThoughtItemProps = {
  config: (typeof MEILI_SEARCH_CONFIG)[keyof typeof MEILI_SEARCH_CONFIG];
  hit: MeiliDocumentSearchHit;
  isInSearchContext?: boolean;
  onDeleteSearchResultAction?: (thoughtId: string) => void;
  onDeleteThoughtSuccess?: () => void;
  onThoughtUpdateSuccess?: () => void;
  sync_meili_index?: MeiliIndex;
  resetReflections?: boolean;
  searchQuery?: string;
  isMobileActionsOpen?: boolean;
  onMobileActionsOpenChange?: (isOpen: boolean) => void;
};

function getDraftValidationMessage(draftMindstring: string): string | null {
  const trimmedMindstring = draftMindstring.trim();

  if (trimmedMindstring.length > appSettings.validation.MINSTRING_MAX_LENGTH) {
    return `Thought is too long. Maximum length is ${appSettings.validation.MINSTRING_MAX_LENGTH} characters.`;
  }

  if (trimmedMindstring.length < appSettings.validation.MINSTRING_MIN_LENGTH) {
    return `Thought is too short. Minimum length is ${appSettings.validation.MINSTRING_MIN_LENGTH} characters.`;
  }

  return null;
}

function SuggestedThoughtItem({
  config,
  hit,
  isInSearchContext = true,
  onDeleteSearchResultAction,
  sync_meili_index = "suggested_thoughts",
  onDeleteThoughtSuccess,
  resetReflections = false,
  onThoughtUpdateSuccess,
  searchQuery,
  isMobileActionsOpen: controlledMobileActionsOpen,
  onMobileActionsOpenChange,
}: SuggestedThoughtItemProps) {
  const { isMobileDevice } = useSystemDesign();
  const { searchQuery: studioSuggestedSearchQuery } = useSearchQueryCheck();
  const suggestedThought = hit as MeiliSuggestedThought;
  const [isEditing, setIsEditing] = useState(false);
  const [localMobileActionsOpen, setLocalMobileActionsOpen] = useState(false);
  const [draftMindstring, setDraftMindstring] = useState(
    suggestedThought.mindstring,
  );
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);
  const didPointerMoveRef = useRef(false);
  const situationsQuery = useSituationsQuery();
  const {
    isSelectedSuggestedThought,
    toggleSelectedSuggestedThought,
    removeSelectedSuggestedThoughts,
    interimState,
    resetInterimState,
  } = useUserChoices();
  const { syncSuggestedThoughtConfirmationViews } = useThoughtRefresh();
  const { confirmSingleThoughtConfirmation, setConfirmationPrototypes } =
    useSuggestedThoughtConfirmationContext();
  const detailSearchQuery = isInSearchContext
    ? (searchQuery ?? studioSuggestedSearchQuery.trim())
    : searchQuery;
  const validSituationIds = useMemo(
    () => (situationsQuery.data ?? []).map((situation) => situation.id),
    [situationsQuery.data],
  );
  const validSituationIdSet = useMemo(
    () => new Set(validSituationIds),
    [validSituationIds],
  );
  const needsAnchor =
    !suggestedThought.situation_id ||
    (situationsQuery.isSuccess &&
      !validSituationIdSet.has(suggestedThought.situation_id));
  const {
    handleSuggestedThoughtsDeleteClick,
    handleSuggestedThoughtsConfirmClick,
  } = useSuggestedThoughtActions({
    confirmSuggestedThoughtConfirmation: confirmSingleThoughtConfirmation,
    setConfirmSuggestedThoughtPrototypes: setConfirmationPrototypes,
    validSituationIds: situationsQuery.isSuccess
      ? validSituationIds
      : undefined,
  });

  const isReviewSuggestionsGuidanceActive =
    interimState.profile_shaping_guidance?.isOpen === true &&
    interimState.profile_shaping_guidance?.value === "review_suggestions";

  const onDeleteSuccessCleanup = useCallback(
    (confirmedThoughtId: string) => {
      onDeleteSearchResultAction?.(confirmedThoughtId);
      onDeleteThoughtSuccess?.();
      removeSelectedSuggestedThoughts([confirmedThoughtId]);
    },
    [
      onDeleteSearchResultAction,
      onDeleteThoughtSuccess,
      removeSelectedSuggestedThoughts,
    ],
  );

  const onUpdateSuccessCleanup = useCallback(
    (confirmedThoughtId: string) => {
      onDeleteSearchResultAction?.(confirmedThoughtId);
      onThoughtUpdateSuccess?.();
      removeSelectedSuggestedThoughts([confirmedThoughtId]);
      syncSuggestedThoughtConfirmationViews(suggestedThought.prototype);
      if (isReviewSuggestionsGuidanceActive) {
        resetInterimState("profile_shaping_guidance");
      }
    },
    [
      isReviewSuggestionsGuidanceActive,
      onDeleteSearchResultAction,
      onThoughtUpdateSuccess,
      removeSelectedSuggestedThoughts,
      resetInterimState,
      suggestedThought.prototype,
      syncSuggestedThoughtConfirmationViews,
    ],
  );

  const confirmSuggestedThought = useCallback(
    async (finalMindstring: string) => {
      const editedMindstring = getEditedMindstring(
        suggestedThought.mindstring,
        finalMindstring,
      );

      if (
        !finalMindstring ||
        finalMindstring.trim().length >
          appSettings.validation.MINSTRING_MAX_LENGTH
      ) {
        showErrorToast(
          `Thought is too long. Maximum length is ${appSettings.validation.MINSTRING_MAX_LENGTH} characters.`,
        );
        return;
      }

      if (
        !finalMindstring ||
        finalMindstring.trim().length <
          appSettings.validation.MINSTRING_MIN_LENGTH
      ) {
        showErrorToast(
          `Thought is too short. Minimum length is ${appSettings.validation.MINSTRING_MIN_LENGTH} characters.`,
        );
        return;
      }

      const request: ConfirmThoughtsRequest = {
        thought_ids: [suggestedThought.objectID],
        edited_mindstring: editedMindstring,
        assign_situation_id: undefined,
        reset_reflections: resetReflections,
      };

      await handleSuggestedThoughtsConfirmClick({
        request,
        onSuccess: () => {
          onUpdateSuccessCleanup(suggestedThought.objectID);
        },
      });
    },
    [
      handleSuggestedThoughtsConfirmClick,
      onUpdateSuccessCleanup,
      resetReflections,
      suggestedThought.mindstring,
      suggestedThought.objectID,
    ],
  );

  const handleConfirmThoughtClick = useCallback(async () => {
    await confirmSuggestedThought(suggestedThought.mindstring);
  }, [confirmSuggestedThought, suggestedThought.mindstring]);

  const handleConfirmEditedSuggestedThought = useCallback(
    async (draftMindstring: string) => {
      await confirmSuggestedThought(draftMindstring);
    },
    [confirmSuggestedThought],
  );

  const handleDelete = useCallback(async () => {
    await handleSuggestedThoughtsDeleteClick(
      {
        thought_ids: [suggestedThought.objectID],
        delete_mode: "permanent",
        sync_meili_index: sync_meili_index,
      },
      undefined,
      [suggestedThought.prototype],
    );
    onDeleteSuccessCleanup(suggestedThought.objectID);
  }, [
    handleSuggestedThoughtsDeleteClick,
    onDeleteSuccessCleanup,
    suggestedThought.objectID,
    suggestedThought.prototype,
    sync_meili_index,
  ]);

  const isSelected = isSelectedSuggestedThought(suggestedThought.objectID);
  const backgroundColor = "moveon.surface.bg";
  const confirmActionLabel = needsAnchor
    ? PROFILE_SHAPING_LABELS.anchorAndConfirm
    : "Confirm";
  const confirmActionHumanReadable = needsAnchor
    ? "Anchor and confirm suggested thought"
    : "Confirm suggested thought into your profile";
  const confirmActionDescription = needsAnchor
    ? PROFILE_SHAPING_LABELS.anchorAndConfirm
    : "Confirm";
  const draftValidationMessage = getDraftValidationMessage(draftMindstring);
  const hasDraftChanges = draftMindstring !== suggestedThought.mindstring;
  const isMobileActionsOpen =
    controlledMobileActionsOpen ?? localMobileActionsOpen;
  const confirmDraftDisabledReason = draftValidationMessage
    ? draftValidationMessage
    : !hasDraftChanges
      ? "Make a change before confirming."
      : null;

  useEffect(() => {
    if (!isEditing) {
      return;
    }

    const focusHandle = requestAnimationFrame(() => {
      const textarea = textareaRef.current;
      if (!textarea) {
        return;
      }

      textarea.focus();
      const cursorPosition = textarea.value.length;
      textarea.setSelectionRange(cursorPosition, cursorPosition);
    });

    return () => cancelAnimationFrame(focusHandle);
  }, [isEditing]);

  const setMobileActionsOpen = useCallback(
    (isOpen: boolean) => {
      setLocalMobileActionsOpen(isOpen);
      onMobileActionsOpenChange?.(isOpen);
    },
    [onMobileActionsOpenChange],
  );

  const handleStartEdit = useCallback(() => {
    setDraftMindstring(suggestedThought.mindstring);
    setMobileActionsOpen(true);
    setIsEditing(true);
  }, [setMobileActionsOpen, suggestedThought.mindstring]);

  const handleCancelEdit = useCallback(() => {
    setDraftMindstring(suggestedThought.mindstring);
    setIsEditing(false);
    setMobileActionsOpen(isMobileDevice);
  }, [isMobileDevice, setMobileActionsOpen, suggestedThought.mindstring]);

  const handleCardPointerDown = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      pointerStartRef.current = {
        x: event.clientX,
        y: event.clientY,
      };
      didPointerMoveRef.current = false;
    },
    [],
  );

  const handleCardPointerMove = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      const pointerStart = pointerStartRef.current;

      if (!pointerStart) {
        return;
      }

      const deltaX = Math.abs(event.clientX - pointerStart.x);
      const deltaY = Math.abs(event.clientY - pointerStart.y);

      if (
        deltaX > MOBILE_ACTION_TOGGLE_POINTER_MOVE_THRESHOLD ||
        deltaY > MOBILE_ACTION_TOGGLE_POINTER_MOVE_THRESHOLD
      ) {
        didPointerMoveRef.current = true;
      }
    },
    [],
  );

  const handleCardClick = useCallback(() => {
    if (!isMobileDevice || isEditing || didPointerMoveRef.current) {
      didPointerMoveRef.current = false;
      pointerStartRef.current = null;
      return;
    }

    setMobileActionsOpen(!isMobileActionsOpen);
    pointerStartRef.current = null;
  }, [isEditing, isMobileActionsOpen, isMobileDevice, setMobileActionsOpen]);

  const handleCardKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (!isMobileDevice || isEditing) {
        return;
      }

      if (event.key !== "Enter" && event.key !== " ") {
        return;
      }

      event.preventDefault();
      setMobileActionsOpen(!isMobileActionsOpen);
    },
    [isEditing, isMobileActionsOpen, isMobileDevice, setMobileActionsOpen],
  );

  const handleConfirmDraft = useCallback(async () => {
    if (confirmDraftDisabledReason) {
      return;
    }

    await handleConfirmEditedSuggestedThought(draftMindstring);
    setIsEditing(false);
  }, [
    confirmDraftDisabledReason,
    draftMindstring,
    handleConfirmEditedSuggestedThought,
  ]);

  const resolvedActions = useSuggestedThoughtItemActions({
    thoughtText: suggestedThought.mindstring,
    confirmActionLabel,
    confirmActionHumanReadable,
    onConfirmAction: handleConfirmThoughtClick,
    onDeleteAction: handleDelete,
    onEditAction: handleStartEdit,
    isConfirmEmphasized: isReviewSuggestionsGuidanceActive,
  });

  const previewContent = (
    <Box
      color="moveon.surface.fg"
      css={
        isInSearchContext && !detailSearchQuery
          ? CONTENT_ITEM_HIGHLIGHT_STYLES
          : undefined
      }
    >
      {detailSearchQuery ? (
        <Box lineClamp={THOUGHT_PREVIEW_LINE_CLAMP}>
          <ThoughtHighlightedText
            searchQuery={detailSearchQuery}
            text={suggestedThought.mindstring}
          />
        </Box>
      ) : (
        <Text
          lineClamp={THOUGHT_PREVIEW_LINE_CLAMP}
          {...CONTENT_ITEM_TEXT_PROPS}
        >
          {isInSearchContext ? (
            <Highlight
              attribute={config.mainContentField}
              hit={hit}
              highlightedTagName="mark"
            />
          ) : (
            suggestedThought.mindstring
          )}
        </Text>
      )}
    </Box>
  );

  const card = (
    <ContentItemCard
      role={isMobileDevice ? "button" : undefined}
      tabIndex={isMobileDevice ? 0 : undefined}
      width="full"
      selected={isSelected}
      bg="moveon.surface.bg"
      selectedBg="moveon.surface.bg"
      selectedBorderColor="moveon.secondary"
      selectedOutlineWidth="0"
      borderWidth="1"
      accentColor={needsAnchor ? "moveon.warning" : undefined}
      cursor={isMobileDevice ? "pointer" : undefined}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      onPointerDown={handleCardPointerDown}
      onPointerMove={handleCardPointerMove}
      _hover={CONTENT_ITEM_HOVER_STYLES}
      _focusVisible={CONTENT_ITEM_FOCUS_VISIBLE_STYLES}
    >
      <VStack align="stretch" gap="3" width="full">
        <Flex align="start" gap="3" width="full">
          <Box flex="1" minW={0}>
            {needsAnchor && (
              <ContentItemUnassignedCallout
                label={PROFILE_SHAPING_LABELS.needsAnchor}
                mb="2"
                // message="Suggested thought without assigned situation"
              />
            )}
            {previewContent}
          </Box>

          <Box marginInlineStart="auto">
            <Box
              display={{ base: "block", lg: "none" }}
              color="moveon.text.muted"
            >
              <GenericIcon
                // icon="three_dots_vertical"
                icon="scroll_down"
                humanReadable={
                  isMobileActionsOpen
                    ? "Hide suggested thought actions"
                    : "Show suggested thought actions"
                }
              />
            </Box>
            <Box
              display={{ base: "none", lg: "block" }}
              onClick={(event) => event.stopPropagation()}
              onPointerDown={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              <ContentItemPreviewActions actions={resolvedActions} />
            </Box>
          </Box>
        </Flex>

        {isMobileActionsOpen && !isEditing ? (
          <Flex
            display={{ base: "flex", lg: "none" }}
            gap="2"
            wrap="nowrap"
            width="full"
            justify="flex-end"
            onClick={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
          >
            {[...resolvedActions].reverse().map((action) => (
              <Button
                key={action.key}
                type="button"
                size="sm"
                variant={action.emphasized ? "solid" : "outline"}
                bg={action.emphasized ? "moveon.primary" : undefined}
                color={
                  action.emphasized
                    ? "white"
                    : action.tone === "danger"
                      ? "moveon.danger"
                      : action.color
                }
                borderColor={
                  action.tone === "danger"
                    ? "moveon.dangerBorder"
                    : "moveon.surface.border"
                }
                flex="0 0 auto"
                minW="10"
                width="10"
                height="10"
                px="0"
                disabled={action.disabled}
                onClick={() => {
                  void action.onClick();
                }}
                aria-label={action.humanReadable}
                title={action.humanReadable}
              >
                <GenericIcon icon={action.icon} />
              </Button>
            ))}
          </Flex>
        ) : null}

        {isEditing ? (
          <Field.Root
            invalid={Boolean(draftValidationMessage)}
            width="full"
            onClick={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
            onKeyDown={(event) => event.stopPropagation()}
          >
            <VStack align="stretch" gap="3" width="full">
              <Text fontSize="xs" color="moveon.text.muted">
                {confirmDraftDisabledReason ??
                  "Confirm the edited suggestion into your profile."}
              </Text>
              <Textarea
                ref={textareaRef}
                value={draftMindstring}
                onChange={(event) => {
                  setDraftMindstring(event.target.value);
                }}
                autoresize
                width="full"
                maxW="none"
                minH="20"
                color="moveon.surface.fg"
                bg="moveon.surface.elevatedBg"
                borderColor="moveon.surface.border"
                aria-label="Suggested thought text"
                onKeyDown={(event) => {
                  if (
                    (event.metaKey || event.ctrlKey) &&
                    event.key === "Enter"
                  ) {
                    event.preventDefault();
                    void handleConfirmDraft();
                  }

                  if (event.key === "Escape") {
                    event.preventDefault();
                    handleCancelEdit();
                  }
                }}
              />
              {draftValidationMessage ? (
                <Field.ErrorText>{draftValidationMessage}</Field.ErrorText>
              ) : null}
              <Flex justify="flex-end" gap="2" wrap="wrap">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={handleCancelEdit}
                  aria-label="Cancel editing suggested thought"
                >
                  <GenericIcon icon="return" />
                  <Text as="span" display={{ base: "none", sm: "inline" }}>
                    Cancel
                  </Text>
                </Button>
                <Button
                  type="button"
                  size="sm"
                  bg="moveon.primary"
                  color="white"
                  disabled={Boolean(confirmDraftDisabledReason)}
                  onClick={() => {
                    void handleConfirmDraft();
                  }}
                >
                  <GenericIcon icon="confirm" />
                  <Text as="span" display={{ base: "none", sm: "inline" }}>
                    {confirmActionLabel}
                  </Text>
                </Button>
              </Flex>
            </VStack>
          </Field.Root>
        ) : null}
      </VStack>
    </ContentItemCard>
  );

  const selectableCard = (
    <WithSwipeSelector
      isMobile={isMobileDevice}
      selection={{
        isSelected: isSelectedSuggestedThought(suggestedThought.objectID),
        onToggle: () =>
          toggleSelectedSuggestedThought(suggestedThought.objectID),
        humanReadable: "Select suggested thought for batch operations",
      }}
      action={{
        onSwipe: handleConfirmThoughtClick,
        icon: "confirm",
        color: "moveon.primary",
        humanReadable: confirmActionHumanReadable,
        actionDescription: confirmActionDescription,
      }}
      visual={{
        uncoveredBackgroundColor: backgroundColor,
      }}
    >
      {card}
    </WithSwipeSelector>
  );

  if (!isMobileDevice || resolvedActions.length === 0) {
    return selectableCard;
  }

  return selectableCard;
}

export default memo(SuggestedThoughtItem);
